import { GeofenceLocation } from "@verity/proctoring-option/types";
import {
	BaseListContainer,
	BaseListFooterContainer,
	BaseListHeaderContainer,
	BaseListInfoArea,
	CommonListProps,
} from "./OptionListsComponents";
import i18nPackage from "../../../i18n/i18n";
import {
	CoreButton,
	CoreMenuButton,
	CoreMenuItem,
	CoreRadioCard,
	CoreRadioCardIcon,
	CoreRadioCardSelectIcon,
} from "../../../core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./GeofenceList.module.css";
import { ListItemText, Typography } from "@mui/material";
import BankIcon from "../../images/configuration/bank.svg?react";
import GeofenceModal, { GeofenceLocationWithId } from "../option-modals/GeofenceModal";
import { useFocusElementOnMount } from "../../../hooks";
import GeofenceListIcon from "../../images/configuration/geofence-list.svg?react";
import { getInitialGeofences } from "./geofenceListHelpers";

const MAX_GEOFENCES = 5;

type DefaultGeofenceProps = {
	geofence: GeofenceLocation;
	isSelected: boolean;
	onSelect: () => void;
	onView?: () => void;
	onEdit?: () => void;
	onDelete?: () => void;
};

function DefaultGeofence({ geofence, isSelected, onDelete, onEdit, onSelect, onView }: DefaultGeofenceProps) {
	const [menuShowing, setMenuShowing] = useState<boolean>(false);

	//const translationKey = geofence.isMetric ? "radiusKm" : "radiusMiles";
	const translationKey = geofence.isMetric ? "radiusMeter" : "radiusFeet";

	return (
		<CoreRadioCard
			slotProps={{
				container: { className: styles.geofenceRow },
				button: { className: styles.geofenceButton },
				actionWrapper: { className: styles.geofenceMenuButton },
			}}
			onClick={onSelect}
			selected={isSelected}
			secondaryAction={
				onDelete || onEdit || onView ? (
					<CoreMenuButton
						menuShowing={menuShowing}
						setMenuShowing={setMenuShowing}
						aria-label={i18nPackage.t("editQuizPage.geofenceList.actionAriaLabel")}
					>
						{onView && (
							<CoreMenuItem
								onClick={() => {
									setMenuShowing(false);
									onView();
								}}
								aria-haspopup="dialog"
							>
								<ListItemText primary={i18nPackage.t("editQuizPage.geofenceList.view")} />
							</CoreMenuItem>
						)}
						{onEdit && (
							<CoreMenuItem
								onClick={() => {
									setMenuShowing(false);
									onEdit();
								}}
								aria-haspopup="dialog"
							>
								<ListItemText primary={i18nPackage.t("editQuizPage.geofenceList.edit")} />
							</CoreMenuItem>
						)}
						{onDelete && (
							<CoreMenuItem
								onClick={() => {
									setMenuShowing(false);
									onDelete();
								}}
							>
								<ListItemText primary={i18nPackage.t("editQuizPage.geofenceList.remove")} />
							</CoreMenuItem>
						)}
					</CoreMenuButton>
				) : null
			}
		>
			<div className={styles.geofenceIconTitleWrapper}>
				<CoreRadioCardIcon enabled={isSelected} icon={<BankIcon />} />
				<div className={styles.geofenceInfoWrapper}>
					<Typography className={styles.name}>{geofence.name}</Typography>
					<Typography className={styles.radius}>
						{i18nPackage.t(`editQuizPage.geofenceList.${translationKey}`, { radius: geofence.radius.toFixed(0) })}
					</Typography>
				</div>
			</div>
			<CoreRadioCardSelectIcon selected={isSelected} />
		</CoreRadioCard>
	);
}

type GeofenceListProps = {
	initialGeofences: GeofenceLocation[];
	initialSelectedGeofence: GeofenceLocation | undefined;
	onSelectGeofence?: (selectedGeofence: GeofenceLocation | undefined) => void;
	onUpdateGeofence?: (newGeofences: GeofenceLocation[]) => void;

	/**Gets called if the user clicks cancel when there is no initial geofences  */
	onCancelFirstAdd?: () => void;

	/**
	 * Setting = course settings
	 * quizCustom = custom geofence is selected
	 * quizSaved = course geofence is selected
	 */
	mode: "quizCustom" | "setting" | "quizSaved";
} & CommonListProps;

export default function GeofenceList(props: GeofenceListProps) {
	const { mode, onUpdateGeofence, onSelectGeofence, onCancelFirstAdd } = props;
	const { ref } = useFocusElementOnMount<HTMLDivElement>(props.focusOnMount, props.onFocus);
	const [modalShowing, setModalShowing] = useState<boolean>(false);
	const [modalTarget, setModalTarget] = useState<GeofenceLocationWithId | undefined>(undefined);
	const [modalMode, setModalMode] = useState<"view" | "edit" | "add">("view");
	const [selectedGeofenceId, setSelectedGeofenceId] = useState<string | undefined>(undefined);
	const [geofences, setGeofences] = useState<GeofenceLocationWithId[]>([]);

	const selectedGeofence = useMemo(
		() => geofences.find((g) => g.id === selectedGeofenceId),
		[geofences, selectedGeofenceId],
	);

	const stripId = useCallback((geofences: GeofenceLocationWithId[]) => {
		return geofences.map(({ id: _id, ...rest }) => rest as GeofenceLocation);
	}, []);

	const handleAddGeofence = useCallback(
		(updatedGeo: GeofenceLocationWithId) => {
			const newGeofences = [updatedGeo, ...geofences];
			setGeofences(newGeofences);
			setSelectedGeofenceId(updatedGeo.id);

			const { id: _id, ...cleanGeo } = updatedGeo;
			const cleanList = stripId(newGeofences);

			// Sync to parent
			onUpdateGeofence?.(cleanList);
			onSelectGeofence?.(cleanGeo);
			setModalShowing(false);
		},
		[geofences, onSelectGeofence, onUpdateGeofence, stripId],
	);

	const handleUpdateGeofence = useCallback(
		(updatedGeo: GeofenceLocationWithId) => {
			const updatedList = geofences.map((g) => (g.id === updatedGeo.id ? updatedGeo : g));

			//  Update local state
			setGeofences(updatedList);

			const { id: _id, ...cleanGeo } = updatedGeo;

			// Sync to parent
			onUpdateGeofence?.(stripId(updatedList));

			// Only update the selection if the one we edited is the one currently selected
			if (selectedGeofenceId === updatedGeo.id) {
				onSelectGeofence?.(cleanGeo);
			}
			setModalShowing(false);
		},
		[geofences, onSelectGeofence, onUpdateGeofence, selectedGeofenceId, stripId],
	);

	const handleSelectGeofence = useCallback(
		(selected: GeofenceLocationWithId) => {
			setSelectedGeofenceId(selected.id);
			onSelectGeofence?.(selected);
		},
		[onSelectGeofence],
	);

	const handleDeleteLocation = useCallback(
		(geofence: GeofenceLocationWithId) => {
			const newGeofences: GeofenceLocationWithId[] = geofences.filter((g) => g.id !== geofence.id);

			// If we just deleted the only geofence
			if (newGeofences.length === 0) {
				setGeofences([]);
				setSelectedGeofenceId(undefined);
				onUpdateGeofence?.([]);
				onSelectGeofence?.(undefined);

				// Trigger the parent to disable the toggle/option since there's no valid data left
				onCancelFirstAdd?.();
				return;
			}
			const newSelectedGeofence = newGeofences.length > 0 ? newGeofences[0] : undefined;
			setGeofences(newGeofences);
			setSelectedGeofenceId(newSelectedGeofence?.id);
			onUpdateGeofence?.(stripId(newGeofences));
			onSelectGeofence?.(newSelectedGeofence);
		},
		[geofences, onCancelFirstAdd, onSelectGeofence, onUpdateGeofence, stripId],
	);

	const handleOpenModal = useCallback((mode: "view" | "edit" | "add", target?: GeofenceLocationWithId) => {
		setModalMode(mode);
		setModalTarget(target);
		setModalShowing(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		setModalShowing(false);
		if (geofences.length === 0 && modalMode === "add") {
			onCancelFirstAdd?.();
		}
	}, [geofences.length, modalMode, onCancelFirstAdd]);

	const shouldInitialize = useMemo(() => {
		const isDataLoaded = geofences.length > 0;
		const doesSelectionMatch = selectedGeofenceId === (props.initialSelectedGeofence as GeofenceLocationWithId)?.id;
		return !isDataLoaded || !doesSelectionMatch;
	}, [geofences.length, props.initialSelectedGeofence, selectedGeofenceId]);

	// Ref so useEffect isn't complaining about dependencies
	const syncRef = useRef({
		initialSelectedGeofence: props.initialSelectedGeofence,
		initialGeofences: props.initialGeofences,
		mode,
		onSelectGeofence,
		modalShowing,
	});

	syncRef.current = {
		initialSelectedGeofence: props.initialSelectedGeofence,
		initialGeofences: props.initialGeofences,
		mode,
		onSelectGeofence,
		modalShowing,
	};

	useEffect(() => {
		if (!shouldInitialize) {
			return;
		}

		const {
			initialGeofences: latestGeofences,
			initialSelectedGeofence: latestSelectedGeofence,
			mode: latestMode,
			onSelectGeofence: latestOnSelectGeofence,
			modalShowing: latestModalShowing,
		} = syncRef.current;

		const { initialGeofences, initialSelectedGeofence } = getInitialGeofences(latestSelectedGeofence, latestGeofences);
		setSelectedGeofenceId(initialSelectedGeofence?.id);
		setGeofences(initialGeofences);

		latestOnSelectGeofence?.(initialSelectedGeofence);

		// Open modal on mount if there are no preset geofences and its a quiz context
		const isQuizMode = latestMode === "quizCustom" || latestMode === "quizSaved";
		if (isQuizMode && initialGeofences.length === 0 && !latestModalShowing) {
			handleOpenModal("add");
		}
	}, [shouldInitialize, handleOpenModal]);

	const showEdit = useMemo(() => mode === "setting" || mode === "quizCustom", [mode]);
	const showView = useMemo(() => mode === "quizSaved", [mode]);
	const showDelete = useMemo(() => mode === "quizCustom" || mode === "setting", [mode]);

	return (
		<BaseListContainer ref={ref}>
			<BaseListHeaderContainer className={styles.geofenceTitleContainer}>
				<BaseListInfoArea
					title={i18nPackage.t("editQuizPage.geofenceList.title")}
					description={
						mode === "setting"
							? i18nPackage.t("editQuizPage.geofenceList.descriptionSettings")
							: i18nPackage.t("editQuizPage.geofenceList.description")
					}
					icon={<GeofenceListIcon />}
				/>
				{mode !== "quizCustom" && (
					<CoreButton
						onClick={() => handleOpenModal("add")}
						disabled={mode === "setting" && geofences.length >= MAX_GEOFENCES}
						disabledTooltip={i18nPackage.t("editQuizPage.geofenceList.disabledTooltip", { number: MAX_GEOFENCES })}
						aria-haspopup="dialog"
						className={styles.addButton}
					>
						{i18nPackage.t("editQuizPage.geofenceList.addGeofence")}
					</CoreButton>
				)}
			</BaseListHeaderContainer>
			<BaseListFooterContainer>
				{geofences.map((geofence) => {
					return (
						<DefaultGeofence
							key={geofence.id}
							geofence={geofence}
							isSelected={mode === "setting" ? false : selectedGeofence?.id === geofence.id}
							onSelect={() => handleSelectGeofence(geofence)}
							onDelete={showDelete ? () => handleDeleteLocation(geofence) : undefined}
							onView={showView ? () => handleOpenModal("view", geofence) : undefined}
							onEdit={showEdit ? () => handleOpenModal("edit", geofence) : undefined}
						/>
					);
				})}
			</BaseListFooterContainer>
			{modalShowing && (
				<GeofenceModal
					allowEdit={modalMode !== "view"}
					onClose={handleCloseModal}
					presetGeofences={geofences}
					geofence={modalMode === "add" ? undefined : modalTarget}
					onSave={(updatedGeo) => {
						if (modalMode === "add") {
							handleAddGeofence(updatedGeo);
						} else if (modalMode === "edit") {
							handleUpdateGeofence(updatedGeo);
						} else {
							handleCloseModal();
						}
					}}
				/>
			)}
		</BaseListContainer>
	);
}
